// Replays a cached answer through the streamed-answer path, a few characters
// at a time.
//
// A quick-prompt cache hit (Summary / Change Plan, BE quick_prompt_cache) has
// no tokens to stream — the stored answer arrives whole, so it used to appear
// instantly where a live answer types out token by token. The BE marks it with
// a `{cached: true}` frame ahead of the text; after `markCached()`, every chunk
// is fed to `onChunk` in REPLAY_CHUNK_CHARS slices every REPLAY_INTERVAL_MS
// (≈1,000 chars/s — brisk, so a ~6,500-char Change Plan types out in ~6.5 s),
// however many frames the text came in. It reveals like a live answer: same
// store action, same cursor, same markdown once complete.
//
// Without the marker (a live answer) chunks go straight to `onChunk`,
// untouched. `end(fn)` runs `fn` (finalize) once everything queued has been
// revealed; `cancel()` drops whatever is left, as closing the socket drops a
// live stream's remaining tokens.
export const REPLAY_CHUNK_CHARS = 16;
export const REPLAY_INTERVAL_MS = 16;

export default function createChunkReplay(onChunk) {
    let cached = false;
    let queue = '';
    let timer = null;
    let onEnd = null;

    const stop = () => {
        clearInterval(timer);
        timer = null;
    };

    const tick = () => {
        const slice = queue.slice(0, REPLAY_CHUNK_CHARS);
        queue = queue.slice(REPLAY_CHUNK_CHARS);
        onChunk(slice);
        if (!queue) {
            stop();
            const fn = onEnd;
            onEnd = null;
            if (fn) fn();
        }
    };

    return {
        markCached() {
            cached = true;
        },
        push(chunk) {
            if (!chunk) return;
            if (!cached) {
                onChunk(chunk);
                return;
            }
            queue += chunk;
            if (!timer) timer = setInterval(tick, REPLAY_INTERVAL_MS);
        },
        end(fn) {
            if (timer) onEnd = fn;
            else fn();
        },
        cancel() {
            stop();
            queue = '';
            onEnd = null;
        },
        get replaying() {
            return timer !== null;
        },
    };
}
